import { lazy } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import Layout from './components/Layout';
import { isWorkView, type WorkView } from './features/work/views';
// Dashboard je landing — necháváme eager kvůli okamžitému prvnímu vykreslení.
import DashboardPage from './features/dashboard/DashboardPage';
import WarehouseScanPage from './features/warehouse/WarehouseScanPage';
import LocationsPage from './features/warehouse/LocationsPage';
import PurchasePage from './features/purchasing/PurchasePage';
import ShopsPage from './features/purchasing/ShopsPage';
// Ostatní stránky lazy → každá je vlastní chunk (menší úvodní bundle, svižnější start).
// Těžké závislosti (grafy, markdown, QR) se načtou až s konkrétní stránkou.
const ClientsPage = lazy(() => import('./features/clients/ClientsPage'));
const DepartmentsPage = lazy(() => import('./features/departments/DepartmentsPage'));
const UsersPage = lazy(() => import('./features/users/UsersPage'));
const MaterialsPage = lazy(() => import('./features/materials/MaterialsPage'));
const AttendancePage = lazy(() => import('./features/attendance/AttendancePage'));
const SettingsPage = lazy(() => import('./features/settings/SettingsPage'));
const TemplatesPage = lazy(() => import('./features/workflowTemplates/TemplatesPage'));
const KnowledgePage = lazy(() => import('./features/knowledge/KnowledgePage'));
const ModelsPage = lazy(() => import('./features/models/ModelsPage'));
const AuditPage = lazy(() => import('./features/audit/AuditPage'));
const ReportsPage = lazy(() => import('./features/reports/ReportsPage'));
const InvoiceBookPage = lazy(() => import('./features/invoices/InvoiceBookPage'));
const WorkPage = lazy(() => import('./features/work/WorkPage'));
const ProjectDetailPage = lazy(() => import('./features/projects/ProjectDetailPage'));
const StationPage = lazy(() => import('./features/station/StationPage'));
const GraphPage = lazy(() => import('./features/graph/GraphPage'));
const PropItemDetailPage = lazy(() => import('./features/propItems/PropItemDetailPage'));
const RentalAssetsPage = lazy(() => import('./features/rental/RentalAssetsPage'));
const RentalBookingsPage = lazy(() => import('./features/rental/RentalBookingsPage'));
const RentalInquiriesPage = lazy(() => import('./features/rental/RentalInquiriesPage'));
const RentalAnalyticsPage = lazy(() => import('./features/rental/RentalAnalyticsPage'));
const AssistantPage = lazy(() => import('./features/ai/AssistantPage'));
const ScannerPage = lazy(() => import('./features/scanner/ScannerPage'));
const WizardPage = lazy(() => import('./features/projects/WizardPage'));
const InventoryPage = lazy(() => import('./features/scanner/InventoryPage'));
const SuppliersPage = lazy(() => import('./features/suppliers/SuppliersPage'));
const CustomersPage = lazy(() => import('./features/supplierCustomers/CustomersPage'));
const SupplierQuotesPage = lazy(() => import('./features/supplierQuotes/SupplierQuotesPage'));

// Code-based routing (bez file-based pluginu) — snazší na build.
const rootRoute = createRootRoute({ component: Layout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: DashboardPage,
});

const clientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/klienti',
  component: ClientsPage,
});

// Produkční společnosti se sloučily do odběratelů — staré odkazy vedou tam.
const productionCompaniesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/produkcni-spolecnosti',
  beforeLoad: () => { throw redirect({ to: '/klienti', replace: true }); },
});

// Zakázky, tabule, harmonogram a kapacita jsou čtyři řezy jedné stránky.
// Řez je v ?pohled= — zůstává sdílitelný odkazem i po přepnutí.
const projectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/zakazky',
  // stroj + den: plán strojů otevře rovnou daný den (odkaz z přehledu Dnes).
  validateSearch: (search: Record<string, unknown>): { pohled?: WorkView; stroj?: string; den?: string } => ({
    ...(isWorkView(search.pohled) ? { pohled: search.pohled } : {}),
    ...(typeof search.stroj === 'string' ? { stroj: search.stroj } : {}),
    ...(typeof search.den === 'string' ? { den: search.den } : {}),
  }),
  component: WorkPage,
});

const projectDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/zakazky/$projectId',
  component: ProjectDetailPage,
});

// Sloučeno do /zakazky — staré odkazy vedou rovnou na správný řez.
const ganttRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/gantt',
  beforeLoad: () => { throw redirect({ to: '/zakazky', search: { pohled: 'harmonogram' }, replace: true }); },
});

const boardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tabule',
  beforeLoad: () => { throw redirect({ to: '/zakazky', search: { pohled: 'tabule' }, replace: true }); },
});

const stationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stanice',
  component: StationPage,
});

const graphRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/graf',
  component: GraphPage,
});

const propItemDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vyroby/$propItemId',
  component: PropItemDetailPage,
});

// Přejmenováno z „rekvizit" na „výroby" — staré odkazy (i ty v QR štítcích
// a e-mailech) musí dál fungovat.
const propItemLegacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/rekvizity/$propItemId',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/vyroby/$propItemId', params, replace: true });
  },
});

const departmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/departmenty',
  component: DepartmentsPage,
});

const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tym',
  component: UsersPage,
});

const attendanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dochazka',
  component: AttendancePage,
});

const materialsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sklad',
  component: MaterialsPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/nastaveni',
  component: SettingsPage,
});

const templatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sablony',
  component: TemplatesPage,
});

const knowledgeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/znalosti',
  component: KnowledgePage,
});

const modelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/modely',
  component: ModelsPage,
});

const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/zaznam-zmen',
  validateSearch: (s: Record<string, unknown>): { zakazka?: string } => (typeof s.zakazka === 'string' ? { zakazka: s.zakazka } : {}),
  component: AuditPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reporty',
  component: ReportsPage,
});

const invoiceBookRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reporty/faktury',
  component: InvoiceBookPage,
});

const planningRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planovani',
  beforeLoad: () => { throw redirect({ to: '/zakazky', search: { pohled: 'kapacita' }, replace: true }); },
});

const rentalAssetsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pujcovna',
  component: RentalAssetsPage,
});

const rentalBookingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pujcovna/rezervace',
  component: RentalBookingsPage,
});

const rentalInquiriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pujcovna/poptavky',
  component: RentalInquiriesPage,
});

const rentalAnalyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pujcovna/vytizeni',
  component: RentalAnalyticsPage,
});

const assistantRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/asistent',
  component: AssistantPage,
});

const wizardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/zakazky/pruvodce',
  component: WizardPage,
});

// Sklad v telefonu (skener) a pozice ve skladu.
const warehouseScanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sklad/sken',
  component: WarehouseScanPage,
});

const locationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sklad/pozice',
  component: LocationsPage,
});

const purchaseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sklad/nakup',
  component: PurchasePage,
});

const shopsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sklad/obchody',
  component: ShopsPage,
});

const scannerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/skener',
  component: ScannerPage,
});

const inventoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inventura',
  component: InventoryPage,
});

const customersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/odberatele',
  component: CustomersPage,
});

const suppliersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dodavatele',
  component: SuppliersPage,
});

const supplierQuotesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/nabidky-dodavatelu',
  component: SupplierQuotesPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  projectsRoute,
  wizardRoute,
  projectDetailRoute,
  ganttRoute,
  boardRoute,
  stationRoute,
  propItemDetailRoute,
  propItemLegacyRoute,
  clientsRoute,
  productionCompaniesRoute,
  departmentsRoute,
  usersRoute,
  materialsRoute,
  attendanceRoute,
  settingsRoute,
  templatesRoute,
  knowledgeRoute,
  modelsRoute,
  auditRoute,
  reportsRoute,
  invoiceBookRoute,
  planningRoute,
  rentalAssetsRoute,
  rentalBookingsRoute,
  rentalInquiriesRoute,
  rentalAnalyticsRoute,
  assistantRoute,
  scannerRoute,
  inventoryRoute,
  warehouseScanRoute,
  locationsRoute,
  purchaseRoute,
  shopsRoute,
  graphRoute,
  suppliersRoute,
  customersRoute,
  supplierQuotesRoute,
]);

export const router = createRouter({ routeTree });

// Type-safe Linky napříč appkou.
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
